import type { ListBuilderConfiguration } from '../types';

/**
 * Quote line items shown inside the quote list's row expansion (PD-15221).
 *
 * Rendered as an embedded list when a quote row is expanded on the main
 * Quotes page. Data comes from GET /quotes/:id/details (a slim per-quote
 * fetch), so rows exist only for the expanded quote — this is NOT a
 * standalone page list.
 *
 * This config deliberately has its OWN listKey (`quote_expansion_lines`)
 * instead of reusing `order_detail`: saved-search customizations are layered
 * per listKey, so keeping the expansion separate means a tenant restyling
 * the expansion can never accidentally restyle the Order Details line-items
 * card (or vice versa).
 *
 * Default columns are the four the feature was requested for — item,
 * item number, quantity ordered, quantity on hand. Everything else the
 * endpoint returns (manufacturer, NDC, size/strength, price, backordered)
 * is selectable but off by default, per the optional-field convention.
 */
export const QUOTE_EXPANSION_LINES_LIST_CONFIG: ListBuilderConfiguration = {
	lists: [
		{
			id: 'default-quote-expansion-lines',
			cacheTTL: 600000,
			name: 'Quote Line Items',
			type: 'default',
			active: true,
			sortOrder: 0,
			defaultSortId: 'line-number-asc',
			columns: [
				{
					id: 'item',
					headers: [{ id: 'item-header', text: 'Item' }],
					fields: [
						{
							id: 'item-description-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'itemDescription',
									objectTable: 'order_detail',
								},
							},
							styleConfig: {
								size: 'md',
								colorRule: { type: 'theme', themeColor: 'link' },
								fontWeight: 'semibold',
							},
							link: {
								enabled: true,
								type: 'object',
								objectTable: 'item',
								idField: 'itemId',
							},
							displayStyle: 'text',
						},
					],
					sortable: true,
					sortFields: [
						{
							direction: 'ASC',
							fieldName: 'itemDescription',
							objectTable: 'order_detail',
						},
					],
					alignment: 'left',
					minWidth: 250,
					widthConfig: { mode: 'flexible', preset: 'large' },
					verticalAlignment: 'middle',
				},
				{
					id: 'item-number',
					headers: [{ id: 'item-number-header', text: 'Item #' }],
					fields: [
						{
							id: 'item-number-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'itemNumber' },
							},
							styleConfig: {
								size: 'md',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'text',
						},
					],
					sortable: true,
					sortFields: [{ direction: 'ASC', fieldName: 'itemNumber' }],
					alignment: 'left',
					minWidth: 120,
					widthConfig: { mode: 'flexible', preset: 'medium' },
					verticalAlignment: 'middle',
				},
				{
					id: 'qty-ordered',
					headers: [{ id: 'qty-ordered-header', text: 'Qty Ordered' }],
					fields: [
						{
							id: 'qty-ordered-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'quantityOrdered',
									objectTable: 'order_detail',
								},
							},
							styleConfig: {
								size: 'md',
								colorRule: { type: 'theme', themeColor: 'textPrimary' },
								fontWeight: 'semibold',
								formatOptions: { numberFormat: 'auto' },
							},
							displayStyle: 'number',
						},
					],
					sortable: true,
					sortFields: [
						{
							direction: 'DESC',
							fieldName: 'quantityOrdered',
							objectTable: 'order_detail',
						},
					],
					alignment: 'right',
					minWidth: 100,
					widthConfig: { mode: 'flexible', preset: 'small' },
					verticalAlignment: 'middle',
				},
				{
					id: 'qty-on-hand',
					headers: [{ id: 'qty-on-hand-header', text: 'Qty On Hand' }],
					fields: [
						{
							id: 'qty-on-hand-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'quantityOnHand' },
							},
							styleConfig: {
								size: 'md',
								colorRule: { type: 'theme', themeColor: 'textPrimary' },
								fontWeight: 'semibold',
								formatOptions: { numberFormat: 'auto' },
							},
							displayStyle: 'number',
						},
					],
					sortable: true,
					sortFields: [{ direction: 'DESC', fieldName: 'quantityOnHand' }],
					alignment: 'right',
					minWidth: 100,
					widthConfig: { mode: 'flexible', preset: 'small' },
					verticalAlignment: 'middle',
				},
			],
			description: 'Line items shown when a quote row is expanded',
			requiredFields: [
				'order_detail._id',
				'order_detail._fk_item__id',
				'order_detail.lineNumber',
			],
		},
	],
	paging: {
		defaultPageSize: 50,
		pageSizeOptions: [25, 50, 100],
	},
	filters: {
		activeFilters: [],
		defaultFilters: {},
	},
	objects: [
		{
			// Item links in the expansion navigate to the item detail page; the
			// resolver needs this entry to substitute :id (see order_detail
			// config for the same pattern).
			baseUrl: '/items/:id',
			objectLabel: 'Item',
			objectTable: 'item',
			selectedFields: [],
		},
		{
			baseUrl: '/quotes/:quoteId',
			objectLabel: 'Quote Line Item',
			objectTable: 'order_detail',
			selectedFields: [
				{ fieldName: '_id', fieldLabel: 'ID' },
				{ fieldName: 'lineNumber', fieldLabel: 'Line Item' },
				{ fieldName: 'itemNumber', fieldLabel: 'Item #' },
				{ fieldName: 'itemDescription', fieldLabel: 'Description' },
				{ fieldName: 'manufacturer', fieldLabel: 'Manufacturer' },
				{ fieldName: 'ndc', fieldLabel: 'NDC' },
				{ fieldName: 'size', fieldLabel: 'Size' },
				{ fieldName: 'strength', fieldLabel: 'Strength' },
				{ fieldName: 'price', fieldLabel: 'Price' },
				{ fieldName: 'quantityOrdered', fieldLabel: 'Qty Ordered' },
				{ fieldName: 'quantityBackordered', fieldLabel: 'Qty Backordered' },
				// Summed across the quote client's warehouses (client_warehouse
				// scoping) by the /quotes/:id/details endpoint.
				{ fieldName: 'quantityOnHand', fieldLabel: 'Qty On Hand' },
				{ fieldName: 'subtotal', fieldLabel: 'Subtotal' },
			],
		},
	],
	tableSorts: [
		{
			id: 'line-number-asc',
			name: 'Line Number',
			fields: [
				{
					direction: 'ASC',
					fieldName: 'lineNumber',
					objectTable: 'order_detail',
				},
			],
			description: 'Sort by line number',
		},
		{
			id: 'description-asc',
			name: 'Description',
			fields: [
				{
					direction: 'ASC',
					fieldName: 'itemDescription',
					objectTable: 'order_detail',
				},
			],
			description: 'Sort by item description',
		},
		{
			id: 'qty-on-hand-desc',
			name: 'Qty On Hand',
			fields: [{ direction: 'DESC', fieldName: 'quantityOnHand' }],
			description: 'Sort by quantity on hand (highest first)',
		},
	],
};
